import type {
  AuthenticationTokens,
  RefreshResponse,
} from '@applied/proto/web_services/candidate_experience/proto/authentication_pb';
import { getCookieDomainFromHostname } from '@applied/utils/cookies';
import { getDecodedClaims } from '@applied/utils/jwt';

const YEAR_IN_MILLISECONDS = 365 * 24 * 60 * 60 * 1000;

/**
 * Updates the client authentication cookies with the new authentication token values.
 */
export function updateAuthenticationCookies(tokens: AuthenticationTokens | RefreshResponse): void {
  // Parse the token claims to figure out the expiration time.
  // NOTE: [exp](https://datatracker.ietf.org/doc/html/rfc7519#section-4.1.4) is a
  // [NumericDate](https://datatracker.ietf.org/doc/html/rfc7519#section-2), a
  // time in seconds since the Unix epoch.
  const identityClaims = getDecodedClaims(tokens.identityToken);
  if (!identityClaims.exp || isNaN(Number(identityClaims.exp))) {
    throw new Error('Identity token expiration time is not set');
  }

  const identityExpiration = new Date(Number(identityClaims.exp) * 1000);

  // Set cookie expiration 1 year after the identity expiration.
  //
  // NOTE: Refresh token authentication requires a SECRET_HASH, which is derived
  // from the user email, client ID, and client secret. The refresh token is opaque,
  // so we can't derive the user email from it. As such, the identity token must live
  // as long as the refresh token, even if it is expired so that we can attempt refresh token
  // authentication.
  const cookieExpiration = new Date(identityExpiration.getTime() + YEAR_IN_MILLISECONDS);

  // If the connection is HTTPS, set the Secure attribute.
  const isSecure = window.location.protocol === 'https:';
  const secureAttribute = isSecure ? ' Secure;' : '';

  // Create the identity token cookie.
  const cookieDomain = getCookieDomainFromHostname(window.location.hostname);
  document.cookie = `IdentityToken=${tokens.identityToken}; expires=${cookieExpiration.toUTCString()}; path=/; domain=${cookieDomain};${secureAttribute} SameSite=Strict`;

  // Only update the refresh token if it is provided (full authentication response).
  if ('refreshToken' in tokens) {
    document.cookie = `RefreshToken=${tokens.refreshToken}; expires=${cookieExpiration.toUTCString()}; path=/; domain=${cookieDomain};${secureAttribute} SameSite=Strict`;
  }
}
